import classes from "./GSATabularView.module.less";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { useComponentSize } from "~/common/hooks/useComponentSize";
import { type ResultCount } from "~/GearboxSelectionAssistant/models";
import { TECH_VALUE_CODE_LIST } from "~/GearboxSelectionAssistant/components/DimensionsFilter/constants";
import {
  calculateVisibleAttributes,
  transformResultCountsToVisibilityMap,
} from "~/GearboxSelectionAssistant/components/ResultsSection/components/GSATabularView/columnConfigUtils";
import { type SrpProductData } from "~/utils/models";
import { toMapFn } from "~/utils/reducers";
import { type ColumnDef, type ColumnSort, type TableState, type Updater } from "@tanstack/react-table";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import { ProductContext } from "~/feature/Product/ProductContext";
import { PreferredIndicator } from "~/feature/Product/elements/PreferredIndicator";
import { Designation } from "~/feature/Product/elements/Designation";
import { useDispatch, useSelector } from "react-redux";
import { selectProductComparisonList } from "~/ProductComparison/redux/productComparisonSelectors";
import { userIsLoggedIn } from "~/utils/roles";
import { addToComparison } from "~/ProductComparison/redux/actions";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { getProductComparisonNavigationEntryUrl } from "~/ProductComparison/productComparisonHelper";
import Checkbox from "~/common/components/Checkbox";
import { Trans } from "react-i18next";
import { getTechFilterTranslations } from "~/GearboxSelectionAssistant/techFilterUtils";
import { makeTabularViewActionColumnDefinition } from "~/GearboxSelectionAssistant/components/ResultsSection/components/GSATabularView/TabularViewActionColumn";

interface Props {
  resultCount: Record<string, ResultCount>;
  results: SrpProductData[];

  sort?: ColumnSort;
  onSortChange: (sort: ColumnSort) => void;
}

type RowData = {
  product: SrpProductData;
  features: Record<string, SrpProductData["features"][0]>;
};

export const GSATabularView = (props: Props) => {
  const { ref, width } = useComponentSize<HTMLDivElement>();

  const possibleAttributes = TECH_VALUE_CODE_LIST;
  const visibilityMap = transformResultCountsToVisibilityMap(props.resultCount);
  const visibleAttributes = calculateVisibleAttributes(possibleAttributes, visibilityMap);

  const rows = props.results.map((product) => ({
    product,
    features: toMapFn(product.features, (f) => f.techFilterId),
  }));

  const isMinTablet = useNumericScreenSize() >= useNumericScreenSize.tablet;

  const productColumn: ColumnDef<RowData> = {
    id: "product",
    header: "Product",
    size: isMinTablet ? 200 : 175,
    accessorFn: (row) => row.product,
    cell: (cell) => {
      const product = cell.getValue() as SrpProductData;
      return (
        <ProductContext value={product}>
          <div className={classes.designationCell}>
            <PreferredIndicator />
            <Designation className={classes.designation} />
          </div>
        </ProductContext>
      );
    },
  };

  const actionColumn: ColumnDef<RowData> = makeTabularViewActionColumnDefinition({
    productGetter: (row) => row.product,
  });

  const compareColumn: ColumnDef<RowData> = {
    id: "compare",
    header: () => (
      <span className={"f-bold"}>
        <Trans i18nKey="compare.label" />
      </span>
    ),
    accessorFn: (row) => row.product,
    cell: (cell) => <ComparisonCell data={cell.getValue() as SrpProductData} />,
    size: 106,
    meta: {
      // columnType: "action",
      align: "center",
    },
  };

  const attributeColumns: ColumnDef<RowData>[] = visibleAttributes.map((code) => {
    const { name, unit, symbol } = getTechFilterTranslations(code);
    const tooltip = (
      <span>
        {name} ({unit})
      </span>
    );
    return {
      id: code,
      header: () => symbol,
      size: isMinTablet ? 100 : 85,
      accessorFn: (row) => row.features[code]?.values,
      enableSorting: true,
      meta: {
        align: "right",
        tooltip,
      },
    };
  });

  const columnConfig = [productColumn, ...attributeColumns, compareColumn, actionColumn];

  const totalLength = columnConfig.reduce((acc, col) => acc + (col.size ?? 0), 0);

  let state: Partial<TableState> = {};
  let fullwidth = true;

  if (totalLength > width) {
    state = { columnPinning: { left: ["product"] } };
    fullwidth = false;
  } else if (totalLength < width) {
    const excess = width - totalLength;
    const columnDelta = Math.floor(excess / (attributeColumns.length + 1));
    [productColumn, ...attributeColumns].forEach((col) => {
      col.size = (col.size ?? 0) + columnDelta;
    });

    const remainder = excess % (attributeColumns.length + 1);
    productColumn.size = (productColumn.size ?? 0) + remainder;
  }

  const sorting = props.sort ? [props.sort] : [];
  state.sorting = sorting;

  const handleSortingChange = (updater: Updater<ColumnSort[]>) => {
    let val;
    if (typeof updater === "function") {
      val = updater(sorting);
    } else {
      val = updater;
    }
    props.onSortChange(val[0]);
  };

  return (
    <div className={classes.root} ref={ref}>
      <TanStackTable
        data={rows}
        columns={columnConfig}
        state={state}
        fullwidth={fullwidth}
        variant={"stripes"}
        manualSorting={true}
        onSortingChange={handleSortingChange}
      />
    </div>
  );
};

const ComparisonCell = ({ data }: { data: SrpProductData }) => {
  const dispatch = useDispatch();
  const productCodes = useSelector(selectProductComparisonList);
  const isChecked = productCodes.some((code) => code === data.code);
  const loggedIn = userIsLoggedIn();
  const handleAddToComparison = () => {
    if (loggedIn) {
      return dispatch(addToComparison(data.code));
    }
    return redirectToUrl(getProductComparisonNavigationEntryUrl());
  };
  return <Checkbox onCheck={handleAddToComparison} checked={isChecked} className={classes.comparison} />;
};
